/* File: ResponsiveTabsList.jsx
 * Path: ananunadm/src/helpers/ResponsiveTabsList.jsx
 *
 * Drop-in replacement for `<Tabs.List>` that adapts to mobile by collapsing
 * the tab strip into a sticky `<Select>` dropdown. On desktop it renders the
 * standard horizontal `Tabs.List` with `Tabs.Tab` children. The component
 * MUST be rendered inside a `<Tabs>` so the desktop branch can use the Tabs
 * context. Provide `value` and `onChange` so the mobile Select can drive the
 * same tab change handler as the desktop tabs.
 */

import React, { useCallback, useLayoutEffect, useRef, useState } from "react";
import { Tabs, Select, Box, useMantineTheme } from "@mantine/core";
import { useResponsive } from "@/helpers/is-mobile";
import tabsClasses from "@/components/Tabs.module.css";

/**
 * @typedef {Object} ResponsiveTabItem
 * @property {string} value          Unique tab value, used by Tabs.Panel.
 * @property {React.ReactNode} label Desktop tab label (can include badges/JSX).
 * @property {string} [selectLabel]  Plain-string label for the mobile Select.
 *                                   Required when `label` is not a string.
 * @property {React.ReactNode} [icon]
 * @property {string} [color]        Mantine color name (Tabs.Tab `color`).
 * @property {React.ReactNode} [rightSection]
 * @property {boolean} [hidden]      When true, the item is excluded entirely.
 * @property {boolean} [disabled]
 * @property {React.Ref<HTMLButtonElement>} [ref]
 */

/**
 * @param {Object} props
 * @param {ResponsiveTabItem[]} props.items
 * @param {string} props.value
 * @param {(value: string) => void} props.onChange
 * @param {boolean} [props.sticky=true] Sticky positioning for the mobile Select.
 * @param {number} [props.stickyTopOffset=0] CSS top offset when sticky.
 * @param {Object} [props.tabsListProps] Extra props forwarded to <Tabs.List>.
 * @param {Object} [props.selectProps]   Extra props forwarded to <Select>.
 */
export function ResponsiveTabsList({
	items,
	value,
	onChange,
	sticky = true,
	stickyTopOffset = 0,
	tabsListProps,
	selectProps
}) {
	const { isMobile } = useResponsive();
	const theme = useMantineTheme();
	const visibleItems = (items || []).filter((item) => item && !item.hidden);

	const listRef = useRef(null);
	const tabRefs = useRef({});
	const [indicator, setIndicator] = useState({ left: 0, top: 0, width: 0, height: 0, opacity: 0 });

	const updateIndicator = useCallback(() => {
		const listNode = listRef.current;
		const activeNode = tabRefs.current[value];
		if (!listNode || !activeNode) {
			setIndicator((prev) => (prev.opacity === 0 ? prev : { ...prev, opacity: 0 }));
			return;
		}
		const listRect = listNode.getBoundingClientRect();
		const activeRect = activeNode.getBoundingClientRect();
		setIndicator({
			left: activeRect.left - listRect.left,
			top: activeRect.top - listRect.top,
			width: activeRect.width,
			height: activeRect.height,
			opacity: 1
		});
	}, [value]);

	const visibleItemsKey = visibleItems.map((item) => item.value).join("|");

	useLayoutEffect(() => {
		updateIndicator();
	}, [updateIndicator, visibleItemsKey, isMobile]);

	useLayoutEffect(() => {
		const listNode = listRef.current;
		if (!listNode || typeof ResizeObserver === "undefined") return undefined;
		const observer = new ResizeObserver(() => updateIndicator());
		observer.observe(listNode);
		return () => observer.disconnect();
	}, [updateIndicator]);

	if (!visibleItems.length) return null;

	if (isMobile) {
		const activeItem = visibleItems.find((i) => i.value === value);
		const activeColorName = activeItem?.color || theme.primaryColor;
		const activeShade = theme.colorScheme === "dark" ? 4 : 6;
		const resolvedActiveColor =
			theme.colors?.[activeColorName]?.[activeShade] ||
			theme.colors?.[theme.primaryColor]?.[activeShade] ||
			"#228be6";
		const selectData = visibleItems.map((item) => ({
			value: item.value,
			label: item.selectLabel || (typeof item.label === "string" ? item.label : String(item.value)),
			disabled: item.disabled
		}));

		return (
			<Box
				style={{
					position: sticky ? "sticky" : "static",
					top: stickyTopOffset,
					zIndex: 5,
					background: theme.colorScheme === "dark" ? theme.colors.dark[7] : theme.white,
					padding: "8px 4px",
					marginBottom: 0,
					borderBottom: "none"
				}}
			>
				<Select
					size="sm"
					radius="md"
					value={value ?? null}
					onChange={onChange}
					data={selectData}
					icon={activeItem?.icon || null}
					styles={{
						input: {
							fontWeight: 600,
							borderColor: resolvedActiveColor,
							background: theme.fn.rgba(
								resolvedActiveColor,
								theme.colorScheme === "dark" ? 0.2 : 0.08
							),
							boxShadow: `0 0 0 1px ${theme.fn.rgba(resolvedActiveColor, theme.colorScheme === "dark" ? 0.35 : 0.2)} inset`
						},
						item: {
							"&[data-selected]": {
								background:
									theme.colorScheme === "dark"
										? theme.fn.rgba(theme.colors.cyan[7], 0.58)
										: theme.fn.rgba(theme.colors.cyan[5], 0.42),
								color: theme.colorScheme === "dark" ? theme.white : theme.colors.cyan[9],
								fontWeight: 600
							}
						}
					}}
					withinPortal
					maxDropdownHeight={320}
					{...selectProps}
				/>
			</Box>
		);
	}

	const listStyle = tabsListProps?.style || {};

	return (
		<Tabs.List
			{...tabsListProps}
			ref={listRef}
			style={{ ...listStyle, flexWrap: "wrap", width: "100%", overflow: "visible", position: "relative" }}
		>
			<span
				className={tabsClasses.tabIndicator}
				style={{
					transform: `translate(${indicator.left}px, ${indicator.top}px)`,
					width: indicator.width,
					height: indicator.height,
					opacity: indicator.opacity
				}}
			/>
			{visibleItems.map((item) => {
				const { value: v, label, icon, color, disabled, rightSection, ref } = item;
				return (
					<Tabs.Tab
						key={v}
						ref={(node) => {
							tabRefs.current[v] = node;
							if (typeof ref === "function") ref(node);
							else if (ref && typeof ref === "object") ref.current = node;
						}}
						value={v}
						color={color}
						icon={icon}
						disabled={disabled}
						rightSection={rightSection}
					>
						{label}
					</Tabs.Tab>
				);
			})}
		</Tabs.List>
	);
}

export default ResponsiveTabsList;
